import {
  ChangeDetectorRef,
  Directive,
  effect,
  inject,
  OnDestroy,
  OnInit,
  Signal,
} from '@angular/core';
import { FormGroup, FormControl, FormBuilder } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  switchMap,
  tap,
} from 'rxjs/operators';
import { SupabaseClientService } from '../services/supabase-client/supabase-client.service';
import { FormEventService } from '../services/form-event-service/form-event';
import { CentralFormRegistryService, FormStatusState } from './form-state.service';
export type FormGroupControlsOf<T> = {
  [K in keyof T]: FormControl<T[K]>;
};

@Directive()
export abstract class AutoSaveFormBase<T> implements OnInit, OnDestroy {
  /** The Reactive Form defined in the child component */
  abstract form: FormGroup;

  /** The name of the Postgres table to call  */
  abstract entityName: string;

  /** * The Source of Truth for the record ID.
   * Child components can define this using input(), toSignal(route.params), etc.
   */
  abstract entityId: Signal<string | number | null>;

  /** State indicators for the UI */
  saving = false;
  lastSaved: Date | null = null;

  private saveSubscription?: Subscription;

  protected supabase = inject(SupabaseClientService);

  cdr = inject(ChangeDetectorRef);

  fb = inject(FormBuilder).nonNullable;
  protected eventsService = inject(FormEventService);


  // Unique identifier for this specific form instance
  protected formInstanceId = Math.random().toString(36).substring(2, 9);

  // Inject the new central registry service
  protected registry = inject(CentralFormRegistryService);

  // Helper method to push status updates to the master button
  private updateRegistry(state: FormStatusState, errors: string[] = []) {
    this.registry.updateFormState(this.formInstanceId, state, errors);
  }
  private globalSaveSubscription?: Subscription;
  ngOnInit() {
    this.setupAutoSave();
    this.setupGlobalSaveListener();
  }

  private setupGlobalSaveListener() {
    this.globalSaveSubscription = this.registry.globalSave$.subscribe(() => {
      // If the form is valid, execute the save bypassing the 1500ms debounce
      if (this.form.valid) {
        this.bypassDebounceAndSave();
      } else {
        // If they click "Save" but a form is locally invalid, catch the validation right away
        this.updateRegistry('error', this.getFormValidationErrors());
      }
    });
  }

  /**
Listens to form changes, debounces, and pushes to Supabase.
   */
  private setupAutoSave() {

    this.form.statusChanges.subscribe((status) => {
      if (status === 'INVALID') {
        const validationErrors = this.getFormValidationErrors();
        this.updateRegistry('error', validationErrors);
      } else if (status === 'VALID' && this.form.pristine) {
        this.updateRegistry('idle');
      }
    });

    this.saveSubscription = this.form.valueChanges
      .pipe(
        tap(() => {
          this.saving = true
        }),
        debounceTime(1500),
        // Only save if form is valid and has actual values
        filter(() => {
          // Don't save if form is invalid

          if (!this._hydration_done) {
            return false;
          }

          if (!this.form.valid) {
            this.updateRegistry('error', this.getFormValidationErrors());
            return false;
          }
          // Don't save if critical fields are missing
          const value = this.form.value;
          if (!value || Object.keys(value).length === 0) {
            return false;
          }
          return true;
        }),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        tap(() => {
          this.saving = true
          this.updateRegistry('saving');
        }),
        switchMap((value) =>
          this.handleSave(value as T).then(
            (data) => ({ success: true, data }),
            (err) => ({ success: false, error: err }) // Convert rejection into a resolved object
          )
        ),
        finalize(() => {
          console.log('DOM component or manual code unsubscribed! Running cleanup...');
        })
      )
      .subscribe({
        next: (result) => {
          if (result.success) {
            this.saving = false;
            this.lastSaved = new Date();
            this.form.markAsPristine();
            this.eventsService.notifySave();
            this.updateRegistry('saved');
          }
          // Handle the caught database error safely without breaking the stream
          else {
            const err = (result as any).error;
            console.error('Auto-save failed safely:', err);
            this.saving = false;
            const errMsg = err?.message || 'Database connection error';
            this.updateRegistry('error', [`[${this.entityName}] ${errMsg}`]);
          }
        },
        error: (err) => {
          console.error('Fatal stream failure:', err);
          this.updateRegistry('error', ['Fatal stream failure:', err]);
        },
      });
  }

  // Helper to extract clean readability validation strings from Angular Form Controls
  getFormValidationErrors(): string[] {
    const messages: string[] = [];
    Object.keys(this.form.controls).forEach((key) => {
      const controlErrors = this.form.get(key)?.errors;
      if (controlErrors) {
        Object.keys(controlErrors).forEach((keyError) => {
          messages.push(`Field "${key}" is invalid: ${keyError}`);
        });
      }
    });
    return messages;
  }

  _hydration_done = false;
  /**
Silent Hydration: Updates the form without triggering the auto-save stream.
   */
  protected hydrate(data: any) {
    if (!data) return;

    // 1. Handle nested arrays first (logic defined in child)
    this.rebuildFormArrays(data);

    // 2. Patch form values WITHOUT emitting an event to valueChanges
    this.form.patchValue(data, { emitEvent: false });

    // 3. Synchronize pristine state so distinctUntilChanged works
    this.form.markAsPristine();

    this.postHydrate(); // Safe to leave empty or implement later

    // This Change detection cycle was removed as it was causing some issues downstream. Namely any lines after this simple failed to execute
    // if (!this.cdr) {
    //   throw new Error('this.cdr (ChangeDetectorRef) is UNDEFINED.');
    // }
    // console.log('Calling cdr.detectChanges()...');
    // this.cdr.detectChanges();

    // Set the hydration flag cleanly. Angular will automatically
    // pick this up and update the DOM safely on the next cycle.
    this._hydration_done = true;
  }
  postHydrate() {
    /* empty */
  }

  /**
Sends the form payload to the Postgres RPC.
   */
  // In AutoSaveFormBase, add this to track all Supabase operations
  protected async handleSave(value: T) {
    const client = this.supabase.client;
    const id = this.form.get('id')?.value || (value as any).id;
    // Don't save if we're trying to update with null values

    if (!this._hydration_done) {
      return;
    }
    let result;
    if (id) {
      // UPDATE existing record
      const { id: _, ...updateData } = value as any;
      result = await client
        .from(this.entityName)
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
    } else {
      // INSERT new record
      result = await client
        .from(this.entityName)
        .insert([value])
        .select()
        .single();
    }

    const { data, error } = result;
    if (error) {
      console.error('Save error:', error);
      console.groupEnd();
      throw error;
    }

    console.groupEnd();
    // Update form ID if this was a new record
    const idControl = this.form.get('id');
    if (!idControl?.value && data?.id) {
      idControl?.patchValue(data.id, { emitEvent: false });
    }
    this.PostCreate();

    return data;
  }
  PostCreate() {
    /* empty */
  }

  /** * Child classes must implement this to clear and repopulate 
FormArrays (e.g., condition steps).
   */
  protected abstract rebuildFormArrays(data: any): void;

  ngOnDestroy() {
    this.saveSubscription?.unsubscribe();
    this.registry.unregisterForm(this.formInstanceId);
  }

  // In AutoSaveFormBase constructor, add logging to see what's being fetched
  constructor() {
    effect(async (onCleanup) => {
      let active = true;
      const currentEntityId = this.entityId();
      const currentEntityName = this.entityName;

      if (!currentEntityId) {
        return;
      }

      try {
        this._hydration_done = false;
        const { data: settingsData, error: settingsError } =
          await this.supabase.client
            .from(currentEntityName)
            .select('*')
            .eq('id', currentEntityId)
            .maybeSingle();

        if (settingsError) {
          console.error('Failed to fetch settings:', settingsError.message);
          return;
        }

        if (settingsData && active) {
          this.hydrate(settingsData);
        }
      } catch (err) {
        console.error('Unexpected error:', err);
      }

      onCleanup(() => {
        active = false;
      });
    });
  }
  //
  public async bypassDebounceAndSave() {
    if (this.form.valid && !this.form.pristine) {
      this.saving = true;
      this.updateRegistry('saving');
      try {
        await this.handleSave(this.form.value as T);
        this.lastSaved = new Date();
        this.form.markAsPristine();
        // TRIGGER REFRESH HERE
        this.eventsService.notifySave();
        this.updateRegistry('saved');
      } catch (err: any) {
        console.error('Manual save failed:', err);
        this.updateRegistry('error', [err?.message || 'Manual save failed']);
      } finally {
        this.saving = false;
      }
    }
  }
}
